'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from 'react';
import type { DownloadExportType, ExportGeneratedEvent, ExportProgressedEvent } from '@/models/download';
import services from '@/services';
import { getEcho } from '@/services/echo';
import { useMainStore } from '@/store';
import { downloadMedia } from '@/utils/common';
import { findOldestPendingDownload, isDownloadStalled, XLSX_MIME_TYPE } from '@/utils/downloads';
type Echo = NonNullable<ReturnType<typeof getEcho>>;
type ExportChannelConfig = {
  exportType: DownloadExportType;
  channelPrefix: string;
  eventName: string;
};
const EXPORT_CHANNELS: ExportChannelConfig[] = [{
  exportType: 'simple',
  channelPrefix: 'export-survey-simple-channel',
  eventName: 'ExportSurveySimpleGenerated'
}, {
  exportType: 'detailed',
  channelPrefix: 'export-survey-detailed-channel',
  eventName: 'ExportSurveyDetailedGenerated'
}];
const PROGRESS_CHANNEL_PREFIX = 'export-survey-progress-channel';
const PROGRESS_EVENT_NAME = 'SurveyExportProgressed';
const DOWNLOADED_DISMISS_DELAY_MS = 6000;
const STALE_DOWNLOAD_MAX_AGE_MS = 60 * 60 * 1000;
const STALL_WATCHDOG_INTERVAL_MS = 15 * 1000;
const handleExportProgressed = (surveyId: string, event: ExportProgressedEvent) => {
  const {
    downloads,
    updateDownload
  } = useMainStore.getState();
  const pendingDownload = findOldestPendingDownload(downloads, surveyId, event.type);
  if (!pendingDownload) return;
  updateDownload(pendingDownload.id, {
    processedCount: event.processed,
    totalCount: event.total,
    estimatedRemainingSeconds: event.etaSeconds && event.etaSeconds > 0 ? event.etaSeconds : undefined,
    lastActivityAt: new Date().toISOString()
  });
};
const handleExportGenerated = async (surveyId: string, exportType: DownloadExportType, event: ExportGeneratedEvent) => {
  const {
    downloads,
    updateDownload
  } = useMainStore.getState();
  const pendingDownload = findOldestPendingDownload(downloads, surveyId, exportType);
  if (!pendingDownload) return;
  updateDownload(pendingDownload.id, {
    status: 'finished',
    filename: event.filename,
    downloadUrl: event.downloadUrl,
    finishedAt: new Date().toISOString()
  });
  try {
    const fileContent = await services.responseRate.downloadGeneratedExport(event.downloadUrl);
    downloadMedia(fileContent, event.filename, XLSX_MIME_TYPE);
    useMainStore.getState().updateDownload(pendingDownload.id, {
      status: 'downloaded'
    });
    setTimeout(() => {
      useMainStore.getState().removeDownload(pendingDownload.id);
    }, DOWNLOADED_DISMISS_DELAY_MS);
  } catch {
    useMainStore.getState().updateDownload(pendingDownload.id, {
      status: 'error'
    });
  }
};
const subscribeToChannel = (echo: Echo, channelName: string) => {
  const separatorIndex = channelName.indexOf('.');
  const channelPrefix = channelName.slice(0, separatorIndex);
  const surveyId = channelName.slice(separatorIndex + 1);
  if (channelPrefix === PROGRESS_CHANNEL_PREFIX) {
    echo.private(channelName).listen(PROGRESS_EVENT_NAME, (event: ExportProgressedEvent) => handleExportProgressed(surveyId, event));
    return;
  }
  const exportChannel = EXPORT_CHANNELS.find(channel => channel.channelPrefix === channelPrefix);
  if (!exportChannel) return;
  echo.private(channelName).listen(exportChannel.eventName, (event: ExportGeneratedEvent) => handleExportGenerated(surveyId, exportChannel.exportType, event));
};
export const DownloadChannelsListener = () => {
  const $ = _c(10);
  const downloads = useMainStore(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const subscribedChannelsRef = useRef(t0);
  let t1;
  let t2;
  if ($[1] !== downloads) {
    const channelNames = new Set();
    for (const download of downloads) {
      if (download.status !== "inProgress") {
        continue;
      }
      const exportChannel = EXPORT_CHANNELS.find(channel => channel.exportType === download.exportType);
      if (!exportChannel) {
        continue;
      }
      channelNames.add(`${exportChannel.channelPrefix}.${download.surveyId}`);
      channelNames.add(`${PROGRESS_CHANNEL_PREFIX}.${download.surveyId}`);
    }
    t2 = [...channelNames].sort().join(",");
    $[1] = downloads;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  t1 = t2;
  const pendingChannelsKey = t1;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(_temp2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(_temp4, t4);
  let t5;
  let t6;
  if ($[5] !== pendingChannelsKey) {
    t5 = () => {
      const echo = getEcho();
      if (!echo) {
        return;
      }
      const wantedChannels = new Set(pendingChannelsKey ? pendingChannelsKey.split(",") : []);
      const subscribedChannels = subscribedChannelsRef.current;
      for (const channelName of [...subscribedChannels]) {
        if (wantedChannels.has(channelName)) {
          continue;
        }
        echo.leave(channelName);
        subscribedChannels.delete(channelName);
      }
      for (const channelName_0 of wantedChannels) {
        if (subscribedChannels.has(channelName_0)) {
          continue;
        }
        subscribeToChannel(echo, channelName_0);
        subscribedChannels.add(channelName_0);
      }
    };
    t6 = [pendingChannelsKey];
    $[5] = pendingChannelsKey;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const subscribedChannels_0 = subscribedChannelsRef.current;
      return () => {
        const echo_0 = getEcho();
        if (!echo_0) {
          return;
        }
        for (const channelName_1 of subscribedChannels_0) {
          echo_0.leave(channelName_1);
        }
        subscribedChannels_0.clear();
      };
    };
    t8 = [];
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  return null;
};
function _temp(state) {
  return state.downloads;
}
function _temp2() {
  const {
    downloads: persistedDownloads,
    removeDownload
  } = useMainStore.getState();
  const staleCutoff = Date.now() - STALE_DOWNLOAD_MAX_AGE_MS;
  for (const download_0 of persistedDownloads) {
    const isLeftOver = download_0.status === "downloaded";
    const isStale = Date.parse(download_0.createdAt) < staleCutoff;
    if (isLeftOver || isStale) {
      removeDownload(download_0.id);
    }
  }
}
function _temp3() {
  const {
    downloads: currentDownloads,
    updateDownload
  } = useMainStore.getState();
  const now = Date.now();
  for (const download_1 of currentDownloads) {
    if (isDownloadStalled(download_1, now)) {
      updateDownload(download_1.id, {
        status: "error"
      });
    }
  }
}
function _temp4() {
  const intervalId = window.setInterval(_temp3, STALL_WATCHDOG_INTERVAL_MS);
  return () => window.clearInterval(intervalId);
}